import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalBody,
  ModalFooter,
  Input,
} from 'reactstrap';
import {
  AlertTriangle,
  XCircle,
  UploadCloud,
  X,
  Plus,
  Trash2,
} from 'lucide-react';

import Loading from 'components/App/Loading';
import mediaApi from 'api/mediaApi';
import { showToastError } from 'utils';

import styles from './ModalComplaintAction.module.scss';

const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png'];
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

const isValidImageFile = (file) => {
  if (!file) return false;
  if (ALLOWED_TYPES.includes(file.type?.toLowerCase())) return true;
  const ext = file.name?.split('.').pop()?.toLowerCase();
  return ALLOWED_EXTENSIONS.includes(ext);
};

function ModalRejectComplaint(props) {
  const {
    isOpen,
    isLoading,
    complaintId,
    showUploadImages,
    onClose,
    onConfirm,
  } = props;

  const [note, setNote] = useState('');
  const [responseImages, setResponseImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const fileInputRef = useRef(null);

  const handleReset = () => {
    setNote('');
    setResponseImages([]);
    setIsUploading(false);
    setIsDragOver(false);
    setHasSubmitted(false);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    const validFiles = fileArray.filter(isValidImageFile);

    if (validFiles.length < fileArray.length) {
      showToastError('Chỉ cho phép tải lên file định dạng JPG, JPEG, PNG!');
    }

    if (validFiles.length === 0) {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    try {
      setIsUploading(true);
      const fileList = validFiles.map((file) => ({ file }));
      const response = await mediaApi.uploadMultipleMedia(fileList);

      const uploadedUrls = [];
      const dataList = response.payload || response.data || response || [];
      const listToExtract = Array.isArray(dataList) ? dataList : [dataList];

      listToExtract.forEach((item) => {
        if (typeof item === 'string') {
          uploadedUrls.push(item);
        } else if (item?.url) {
          uploadedUrls.push(item.url);
        } else if (item?.location) {
          uploadedUrls.push(item.location);
        } else if (item?.path) {
          uploadedUrls.push(item.path);
        }
      });

      setResponseImages((prev) => [...prev, ...uploadedUrls]);
    } catch (err) {
      showToastError(err?.message || 'Tải ảnh lên thất bại!');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileChange = (e) => {
    uploadFiles(e.target.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      uploadFiles(e.dataTransfer.files);
    }
  };

  const handleRemoveImage = (indexToRemove) => {
    setResponseImages((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handleSubmit = () => {
    setHasSubmitted(true);
    if (!note.trim()) {
      return;
    }

    onConfirm({
      status: 'SKIPPED',
      response: note.trim(),
      responseImages,
    });
  };

  const isNoteInvalid = hasSubmitted && !note.trim();

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      centered
      className={styles.ModalDialog}
    >
      <Loading loading={isLoading || isUploading} loaderOnPage />

      <div className={styles.ModalHeader}>
        <div className={styles.HeaderLeft}>
          <div className={`${styles.HeaderIconBox} ${styles.HeaderIconBoxDanger}`}>
            <AlertTriangle size={24} />
          </div>
          <div className={styles.HeaderText}>
            <h5 className={styles.HeaderTitle}>Từ chối khiếu nại</h5>
            <span className={styles.HeaderSubtitle}>{`Mã khiếu nại: #${complaintId || ''}`}</span>
          </div>
        </div>

        <button
          type="button"
          className={styles.CloseBtn}
          onClick={handleClose}
          aria-label="Đóng"
        >
          <X size={18} />
        </button>
      </div>

      <ModalBody className={styles.ModalBody}>
        <div className={styles.FormGroup}>
          <div className={styles.LabelRow}>
            <label htmlFor="rejectNote" className={styles.FormLabel}>
              Lý do từ chối
              <span className={styles.Required}>*</span>
            </label>
            <span className={styles.CharCounter}>{`${note.length} ký tự`}</span>
          </div>

          <Input
            id="rejectNote"
            type="textarea"
            rows="5"
            className={`${styles.Textarea} ${isNoteInvalid ? styles.Invalid : ''}`}
            placeholder="Nhập chi tiết lý do từ chối giải quyết khiếu nại (ví dụ: khiếu nại không hợp lệ, thông tin cung cấp không chính xác, vượt quá thời hạn quy định...)..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          {isNoteInvalid && (
            <div className={styles.ErrorMessage}>Vui lòng nhập lý do từ chối khiếu nại</div>
          )}
        </div>

        {showUploadImages && (
          <div className={styles.UploadSection}>
            <div className={styles.LabelRow}>
              <span className={styles.FormLabel}>
                Ảnh đính kèm từ chối
                <span className={styles.Optional}>(tùy chọn)</span>
              </span>
              {responseImages.length > 0 && (
                <span className={styles.CharCounter}>{`Đã chọn: ${responseImages.length} ảnh`}</span>
              )}
            </div>

            <input
              ref={fileInputRef}
              id="rejectImageFileInput"
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,image/jpeg,image/png"
              style={{ display: 'none' }}
              onChange={handleFileChange}
              disabled={isUploading}
            />

            {responseImages.length === 0 ? (
              <div
                className={`${styles.DropZone} ${isDragOver ? styles.DragOver : ''}`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
              >
                <div className={styles.UploadIconWrap}>
                  <UploadCloud size={24} />
                </div>
                <div className={styles.UploadMainText}>
                  <span>Nhấn để chọn ảnh</span>
                  {' '}
                  hoặc kéo thả ảnh vào đây
                </div>
                <div className={styles.UploadSubText}>
                  Hỗ trợ định dạng JPG, JPEG, PNG (tối đa 5MB/ảnh)
                </div>
              </div>
            ) : (
              <div className={styles.ThumbGrid}>
                {responseImages.map((url, idx) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={idx} className={styles.ThumbCard}>
                    <img src={url} alt={`Ảnh từ chối ${idx + 1}`} />
                    <span className={styles.ThumbIndex}>{String(idx + 1).padStart(2, '0')}</span>
                    <button
                      type="button"
                      className={styles.DeleteBtn}
                      onClick={() => handleRemoveImage(idx)}
                      title="Xóa ảnh này"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}

                <div
                  className={styles.AddMoreBtn}
                  onClick={() => fileInputRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                  title="Thêm ảnh khác"
                >
                  <Plus size={20} />
                  <span>Thêm ảnh</span>
                </div>
              </div>
            )}
          </div>
        )}
      </ModalBody>

      <ModalFooter className={styles.ModalFooter}>
        <button
          type="button"
          className={styles.CancelBtn}
          onClick={handleClose}
          disabled={isLoading || isUploading}
        >
          Hủy
        </button>
        <button
          type="button"
          className={styles.RejectBtn}
          onClick={handleSubmit}
          disabled={isLoading || isUploading}
        >
          <XCircle size={16} />
          <span>Xác nhận từ chối</span>
        </button>
      </ModalFooter>
    </Modal>
  );
}

ModalRejectComplaint.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool,
  complaintId: PropTypes.string,
  showUploadImages: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

ModalRejectComplaint.defaultProps = {
  isLoading: false,
  complaintId: '',
  showUploadImages: true,
};

export default ModalRejectComplaint;
